import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { PageHeader } from '../shell';
import { DataTable, Modal, useToast } from '../organisms';
import { useEntityPermission } from '../lib/usePermissions';
import { formatDate } from '../lib/orgTimezone';

/**
 * ExemptionProposals — /v2/parcels/exemption-proposals ("Government mass update")
 *
 * The BULK government-mass-update surface (backlog 10.6 + Q6+Q7): the list of
 * mass-update proposals + the "propose" flow. ONE spine drives three actions
 * (proposal.action_type):
 *   • exemption — a TARGET flag delta (homestead / over_65);
 *   • cap       — a per-parcel appraised-value cap ($ abatement);
 *   • reduction — a per-parcel recorded tax reduction ($).
 * across a selected set of parcels, moving proposed → applied | cancelled.
 *
 * Wired to the REAL backend (src/routes/exemption-proposals.js):
 *   • list     GET  /api/exemption-proposals
 *   • propose  POST /api/exemption-proposals { action_type, parcel_ids, note?,
 *                    homestead?/over_65? (exemption) | target_amount (cap/reduction) }
 * The propose modal selects parcels from the REAL GET /api/parcels list. On create it
 * routes to the detail view to preview the computed impact, edit per-parcel overrides,
 * and sign off.
 */

export type ProposalStatus = 'proposed' | 'applied' | 'cancelled';
export type ActionType = 'exemption' | 'cap' | 'reduction';

export interface ProposalOverride { amount?: number }

export interface ExemptionProposal {
  id: string;
  status: ProposalStatus;
  action_type: ActionType;
  set_homestead: boolean | null;
  set_over_65: boolean | null;
  target_amount: number | null;       // governed money — stripped for a money-blind role
  target_percentage: number | null;
  overrides: Record<string, ProposalOverride>;
  parcel_ids: string[];
  parcel_count: number;
  note: string | null;
  created_by_user_id: string | null;
  applied_by_user_id: string | null;
  applied_at: string | null;
  created_at: string;
  updated_at: string;
}

export const ACTION_LABEL: Record<ActionType, string> = {
  exemption: 'Exemption flags',
  cap: 'Cap',
  reduction: 'Reduction',
};

export function money(v: number | null | undefined): string {
  return v == null ? '—' : `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

interface ParcelLite {
  id: string;
  account_number: string;
  name: string | null;
}
interface Paginated<T> { rows: T[]; total: number; page: number; limit: number }

const STATUS_VARIANT: Record<ProposalStatus, 'success' | 'warning' | 'neutral'> = {
  proposed: 'warning',
  applied: 'success',
  cancelled: 'neutral',
};

function fmtDate(v: string | null | undefined): string {
  return formatDate(v);
}

/** Render the target change as a compact human label (action-aware). */
function targetLabel(p: ExemptionProposal): string {
  if (p.action_type === 'cap' || p.action_type === 'reduction') {
    if (p.target_amount != null) return money(p.target_amount);
    if (p.target_percentage != null) return `${p.target_percentage}%`;
    return 'per-parcel'; // overrides-only, or money hidden for a money-blind role
  }
  const parts: string[] = [];
  const push = (label: string, v: boolean | null) => { if (v !== null) parts.push(`${v ? '+' : '−'}${label}`); };
  push('homestead', p.set_homestead);
  push('over-65', p.set_over_65);
  return parts.length ? parts.join(', ') : '—';
}

export function ExemptionProposals() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const canEdit = useEntityPermission('parcel', 'can_edit');

  const listQuery = useQuery<ExemptionProposal[]>({
    queryKey: ['exemption-proposals'],
    queryFn: ({ signal }) => api.get<ExemptionProposal[]>('/api/exemption-proposals', signal),
  });

  const columns = useMemo<ColumnDef<ExemptionProposal>[]>(() => [
    { accessorKey: 'created_at', header: 'Created', cell: ({ getValue }) => fmtDate(getValue() as string) },
    {
      id: 'action',
      header: 'Action',
      cell: ({ row }) => <Badge variant="info">{ACTION_LABEL[row.original.action_type] ?? row.original.action_type}</Badge>,
    },
    { id: 'target', header: 'Target change', cell: ({ row }) => targetLabel(row.original) },
    { accessorKey: 'parcel_count', header: 'Parcels', cell: ({ getValue }) => (getValue() as number).toLocaleString() },
    {
      accessorKey: 'note',
      header: 'Note',
      cell: ({ getValue }) => (getValue() as string | null) || <span style={{ color: 'var(--color-muted)' }}>—</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = getValue() as ProposalStatus;
        return <Badge variant={STATUS_VARIANT[s]} dot>{s}</Badge>;
      },
    },
  ], []);

  const rows = listQuery.data ?? [];

  return (
    <>
      <PageHeader
        title="Government mass update"
        subtitle={listQuery.isLoading ? 'Loading…' : `${rows.length.toLocaleString()} proposal${rows.length === 1 ? '' : 's'}`}
        actions={canEdit ? (
          <Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>
            + Propose mass update
          </Button>
        ) : undefined}
      />

      {listQuery.isError && (
        <p style={{ color: 'var(--color-danger)' }}>Failed to load proposals.</p>
      )}
      {!listQuery.isLoading && rows.length === 0 ? (
        <div style={{
          padding: 'var(--sp-6)', textAlign: 'center', color: 'var(--color-ink-3)',
          border: '1px dashed var(--color-border)', borderRadius: 'var(--r-xl)',
        }}>
          No government mass-update proposals yet. Propose one — exemption flags, a cap, or a reduction — to preview the impact across a set of parcels before applying.
        </div>
      ) : (
        <DataTable data={rows} columns={columns} onRowClick={(p) => navigate(`/parcels/exemption-proposals/${p.id}`)} />
      )}

      {createOpen && (
        <ProposeModal
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => { setCreateOpen(false); navigate(`/parcels/exemption-proposals/${id}`); }}
        />
      )}
    </>
  );
}

/**
 * ProposeModal — pick the ACTION (exemption flags / cap / reduction), its target,
 * and a set of parcels, then POST /api/exemption-proposals. For exemption the target
 * is the flag set (mirroring the 10.2 card); for cap/reduction it
 * is a single dollar amount applied across the selection (per-parcel overrides are
 * edited on the detail page before sign-off).
 */
function ProposeModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [action, setAction] = useState<ActionType>('exemption');
  const [homestead, setHomestead] = useState<boolean | null>(null);
  const [over65, setOver65] = useState<boolean | null>(null);
  const [targetAmount, setTargetAmount] = useState('');
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const parcelsQuery = useQuery<Paginated<ParcelLite>>({
    queryKey: ['exemption-propose-parcels', search],
    queryFn: ({ signal }) =>
      api.get<Paginated<ParcelLite>>(`/api/parcels?limit=100${search ? `&q=${encodeURIComponent(search)}` : ''}`, signal),
  });
  const parcels = parcelsQuery.data?.rows ?? [];

  const amountNum = Number(targetAmount);
  const amountValid = targetAmount.trim() !== '' && Number.isFinite(amountNum) && amountNum >= 0;

  const create = useMutation<ExemptionProposal, Error, void>({
    mutationFn: () => {
      const body: Record<string, unknown> = { action_type: action, parcel_ids: [...selected], note: note.trim() || undefined };
      if (action === 'exemption') {
        if (homestead !== null) body.homestead = homestead;
        if (over65 !== null) body.over_65 = over65;
      } else {
        body.target_amount = amountNum;
      }
      return api.post<ExemptionProposal>('/api/exemption-proposals', body);
    },
    onSuccess: (p) => {
      queryClient.invalidateQueries({ queryKey: ['exemption-proposals'] });
      onCreated(p.id);
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error) : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const anyFlagTarget = homestead !== null || over65 !== null;
  const targetReady = action === 'exemption' ? anyFlagTarget : amountValid;
  const canSubmit = targetReady && selected.size > 0 && !create.isPending;

  const toggle = (id: string) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // A tri-state target control: Leave / Set on / Set off.
  const TriState = ({ label, value, onChange, disabled, hint }: {
    label: string; value: boolean | null; onChange: (v: boolean | null) => void; disabled?: boolean; hint?: string;
  }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <span style={{ minWidth: 120, fontWeight: 'var(--fw-medium)' }}>{label}</span>
        {(['leave', 'on', 'off'] as const).map((opt) => {
          const optVal = opt === 'leave' ? null : opt === 'on';
          const isDisabled = disabled && opt === 'on';
          return (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-1)', opacity: isDisabled ? 0.4 : 1 }}>
              <input
                type="radio"
                name={`tri-${label}`}
                checked={value === optVal}
                disabled={isDisabled}
                onChange={() => onChange(optVal)}
              />
              <span style={{ fontSize: 'var(--fs-sm)', textTransform: 'capitalize' }}>{opt === 'leave' ? 'Leave' : `Set ${opt}`}</span>
            </label>
          );
        })}
      </div>
      {hint && <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', marginLeft: 120 }}>{hint}</span>}
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Propose government mass update"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>Cancel</Button>
          <Button variant="primary" onClick={() => create.mutate()} disabled={!canSubmit}>
            {create.isPending ? 'Proposing…' : `Propose for ${selected.size} parcel${selected.size === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <section>
          <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>Action</h3>
          <Select
            aria-label="Mass update action"
            value={action}
            onChange={(e) => setAction(e.target.value as ActionType)}
            options={[
              { value: 'exemption', label: 'Exemption flags' },
              { value: 'cap', label: 'Cap (appraised-value abatement)' },
              { value: 'reduction', label: 'Reduction (recorded tax reduction)' },
            ]}
          />
        </section>

        {action === 'exemption' ? (
          <section>
            <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>Target change</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
              <TriState label="Homestead" value={homestead} onChange={setHomestead}
                hint="FS-1 residential homestead exemption." />
              <TriState label="Over 65" value={over65} onChange={setOver65}
                hint="Stacks on top of homestead." />
            </div>
          </section>
        ) : (
          <section>
            <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
              {action === 'cap' ? 'Cap amount' : 'Reduction amount'}
            </h3>
            <Input
              type="number"
              min="0"
              aria-label={action === 'cap' ? 'Cap amount' : 'Reduction amount'}
              placeholder={action === 'cap' ? 'Abatement $ per parcel' : 'Reduction $ per parcel'}
              value={targetAmount}
              onChange={(e) => setTargetAmount(e.target.value)}
            />
            <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              Applied to every selected parcel. You can override individual parcels on the next screen before signing off.
            </p>
          </section>
        )}

        <section>
          <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>
            Parcels ({selected.size} selected)
          </h3>
          <Input placeholder="Search parcels by account / name / owner…" value={search}
            onChange={(e) => setSearch(e.target.value)} />
          <div style={{
            marginTop: 'var(--sp-2)', maxHeight: 260, overflowY: 'auto',
            border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)',
          }}>
            {parcelsQuery.isLoading ? (
              <p style={{ padding: 'var(--sp-3)', color: 'var(--color-ink-3)' }}>Loading parcels…</p>
            ) : parcels.length === 0 ? (
              <p style={{ padding: 'var(--sp-3)', color: 'var(--color-ink-3)' }}>No parcels match.</p>
            ) : parcels.map((p) => (
              <label key={p.id} style={{
                display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
                padding: 'var(--sp-2) var(--sp-3)', borderBottom: '1px solid var(--color-border)', cursor: 'pointer',
              }}>
                <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{p.account_number}</span>
                <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>{p.name || ''}</span>
              </label>
            ))}
          </div>
        </section>

        <section>
          <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        </section>
      </div>
    </Modal>
  );
}
